<ion-header>
  <ion-toolbar>
    <ion-title> Rate details </ion-title>
    <ion-buttons slot="end">
      <ion-button (click)="modalCtrl.dismiss()">
        <ion-icon
          size="large"
          name="close"
          [attr.aria-label]="'close'"
          slot="icon-only"
        ></ion-icon>
      </ion-button>
    </ion-buttons>
    <ion-progress-bar
      type="indeterminate"
      *ngIf="loaderService.isLoadingInProgress"
    ></ion-progress-bar>
  </ion-toolbar>
</ion-header>

<ion-content>
  <form [formGroup]="rateDetailsForm">
    <div class="mable-section">
      <!-- Rate type -->
      <mable-input
        label="Select rate type"
        [noBorder]="true"
      >
        <mb-radio-group
          formControlName="rateType"
          [options]="rateTypeOptions"
          radioStyle="oval"
          data-testid="rate-details-select-rate-type-radio"
        >
        </mb-radio-group>
      </mable-input>

      <!-- Flat rate banner -->
      <mable-banner
        category="callout"
        *ngIf="rateTypeControl.value"
      >
        <ng-container class="content">
          <mable-heading headingStyle="h5">What is a flat rate?</mable-heading>
          <p class="ion-no-margin">
            A flat rate means you agree to charge the chosen rate for the entire session, instead of per hour.
          </p>
        </ng-container>
      </mable-banner>

      <!-- Rate name -->
      <mable-input
        label="Rate name"
        [hasError]="rateDetailsForm.get('rateName').showError()"
      >
        <p class="description">
          Enter a short descriptive name eg,
          {{ !rateTypeControl.value ? "'weekday evening'" : "'Saturday sleepover 10 hrs'" }}.
        </p>
        <ion-input
          autocapitalize="sentences"
          autocomplete="on"
          autocorrect="on"
          spellcheck="true"
          type="text"
          formControlName="rateName"
          data-testid="rate-details-rate-name"
          [attr.aria-label]="'Enter a short descriptive rate name'"
        >
        </ion-input>
        <p
          class="warn"
          *ngIf="rateDetailsForm.get('rateName').errors?.required"
        >
          Enter a name for this rate.
        </p>
      </mable-input>

      <!-- Description -->
      <mable-input label="Description (optional)">
        <p class="description">Additional information to describe this rate.</p>
        <ion-input
          autocapitalize="sentences"
          autocomplete="on"
          autocorrect="on"
          spellcheck="true"
          type="text"
          formControlName="rateDescription"
          data-testid="rate-details-rate-description"
          [attr.aria-label]="'additional information to describe this rate'"
        >
        </ion-input>
      </mable-input>

      <!-- Budget Support Types -->
      <div
        class="budget-support"
        *ngIf="!rateTypeControl.value && allBudgetSupportTypesOptions.length"
      >
        <mable-heading
          heading="h3"
          headingStyle="h4"
          >Service categories for this rate</mable-heading
        >
        <p class="description ion-margin-horizontal">
          Service categories are designed to align with NDIS support categories, to streamline payment processing. To
          update this, select from the options below.
        </p>
        <ion-item
          lines="none"
          *ngIf="rateDetailsForm.get('budgetSupportTypeGroup').showError()"
        >
          <ion-icon
            color="red-500"
            name="warning-outline"
          ></ion-icon>
          <ion-label
            color="red-500"
            class="ion-text-wrap half-margin-vertical half-margin-horizontal"
          >
            Select at least 1 service category from the options below.
          </ion-label>
        </ion-item>
        <app-checkbox-group
          [options]="allBudgetSupportTypesOptions"
          (selectedOptionsChange)="onBudgetSelectionChange()"
        >
        </app-checkbox-group>

        <!-- Collapsible section for requesting a non-standard budget support item -->
        <div>
          <app-collapsible
            [isPanelExpanded]="false"
            headingColor="purple-500"
            title="Looking for another service category?"
            class="no-line"
          >
            <ng-container class="content">
              <div>
                <p class="half-margin-vertical">
                  The service categories above are automatically supported for this client’s payments through Mable. Let
                  us know if something is missing.
                </p>
                <mable-button
                  category="link"
                  (click)="openQualtricsForm()"
                  class="ion-no-padding"
                  icon="open"
                  iconSlot="end"
                >
                  Request a service
                </mable-button>
              </div>
            </ng-container>
          </app-collapsible>
        </div>
        <mable-button
          category="link"
          (click)="openMoreInfoAboutServiceCategoriesLink()"
          icon="alert-circle"
        >
          More info about service categories
        </mable-button>
      </div>

      <!-- Hourly rate DoW/ToD schedule -->
      <div
        class="rate-schedule"
        *ngIf="!rateTypeControl.value"
      >
        <!-- Day of week options -->
        <mable-heading
          heading="h3"
          headingStyle="h4"
          >Days this rate is for</mable-heading
        >
        <p class="description ion-margin-horizontal">Select as many as you like.</p>
        <ion-item
          lines="none"
          *ngIf="rateDetailsForm.get('dayOfWeekGroup').showError()"
        >
          <ion-icon
            color="red-500"
            name="warning-outline"
          ></ion-icon>
          <ion-label
            color="red-500"
            class="ion-text-wrap half-margin-vertical half-margin-horizontal"
          >
            Select the days this rate applies to.
          </ion-label>
        </ion-item>
        <app-checkbox-group
          [options]="dayOfWeekOptions"
          (selectedOptionsChange)="onDayOfWeekChange()"
          data-testid="rate-details-rate-schedule-days-of-week"
        ></app-checkbox-group>

        <!-- Time of day options -->
        <mable-heading
          heading="h3"
          headingStyle="h4"
          >Times this rate is for (optional)</mable-heading
        >
        <p class="description ion-margin-horizontal">Select as many as you like.</p>
        <app-checkbox-group
          [options]="timeOfDayOptions"
          (selectedOptionsChange)="onTimeOfDayChange()"
        ></app-checkbox-group>

        <!-- Overnight banner -->
        <mable-banner
          category="headed-banner"
          heading="Is this an active rate?"
          icon="information-circle"
          [canClose]="false"
          *ngIf="showOvernightBanner"
        >
          <ng-container class="content">
            <p>
              Hourly overnight rates are for time spent actively working. If you will be sleeping, create a flat rate
              instead.
            </p>
          </ng-container>
        </mable-banner>
      </div>
      <!-- Rate Amount -->
      <div class="ion-margin-top">
        <mable-input
          [label]="rateDetailsForm.controls.rateType.value ? 'Agreed flat rate' : 'Agreed hourly rate'"
          icon="fa-dollar-sign-solid"
          [class.has-info]="shouldDisplayNewRateInfo"
          [hasError]="isRateAmountInvalid"
        >
          <ion-input
            type="text"
            debounce="500"
            formControlName="rateAmount"
            step="1"
            data-testid="rate-details-rate-amount-input"
            [attr.aria-label]="rateDetailsForm.controls.rateType.value ? 'Agreed flat rate' : 'Agreed hourly rate'"
          >
          </ion-input>
          <div class="warn">
            <p *ngIf="rateDetailsForm.controls.rateAmount.errors?.required">Enter an amount for this rate.</p>
            <p
              *ngIf="
                !rateDetailsForm.controls.rateAmount.errors?.pattern && rateDetailsForm.controls.rateAmount.errors?.max
              "
            >
              Amount must be less than $10,000.
            </p>
            <p
              *ngIf="
                !rateDetailsForm.controls.rateAmount.errors?.pattern && rateDetailsForm.controls.rateAmount.errors?.min
              "
            >
              <ng-container [ngSwitch]="rateDetailsForm.controls.rateType.value">
                <span *ngSwitchCase="true">Please enter a value greater than 0</span>
                <span *ngSwitchCase="false"
                  >Offer a minimum rate of ${{ minimumHourlyRate }} for active support under our terms of use.</span
                >
              </ng-container>
            </p>
            <p *ngIf="rateDetailsForm.controls.rateAmount.errors?.pattern">Provide a numeric value</p>
          </div>
          <p
            class="info"
            *ngIf="!isRateAmountInvalid && shouldDisplayNewRateInfo"
          >
            Net rate you will be paid after fees is ${{ rateDetailsForm.value.netRate }}
          </p>
        </mable-input>

        <!-- Price Limit Banner -->
        <ng-container *ngIf="showCapRateBanner && !rateDetailsForm.controls.rateType.value">
          <app-ndis-price-limit-alert
            [priceLimitAlertData]="priceLimitAlertData"
            [rateData]="rateDetailsForm.value"
          >
          </app-ndis-price-limit-alert>
        </ng-container>
      </div>
    </div>
  </form>
</ion-content>
<ion-footer>
  <ion-toolbar>
    <mable-button
      data-testid="rate-details-save-rate-details"
      size="large"
      expand="block"
      (onClick)="onSubmit()"
      [throttleTime]="1000"
    >
      Save rate details
    </mable-button>
    <mable-button
      expand="block"
      data-testid="rate-details-cancel-button"
      category="outline"
      (onClick)="modalCtrl.dismiss()"
    >
      Cancel
    </mable-button>
  </ion-toolbar>
</ion-footer>
